import LinkIcon from '@mui/icons-material/Link'
import { Box, Card, CardActionArea, Chip, Stack, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { Datagrid, FunctionField, TextField, useStore, useTranslate } from 'react-admin'
import { getVirtualDeviceId } from '../common/virtualDevice'
import { deviceHasMetrics, useDevicesWithMetrics } from '../hooks/useDevicesWithMetrics'
import { usePetDevicesWithHistory } from '../hooks/usePetDevicesWithHistory'
import { useIsCompact } from '../hooks/useResponsive'
import { formatTimezoneForDisplay } from '../utils/timezoneUtils'

interface EnhancedDeviceDatagridProps {
  rowClick?: 'edit' | 'select' | string | ((id: any, resource: any, record: any) => any)
  selectedPetId: string | number | null
}

const EnhancedDeviceDatagrid = ({
  rowClick = 'edit',
  selectedPetId,
}: EnhancedDeviceDatagridProps) => {
  const translate = useTranslate()
  const [selectedDeviceId, setSelectedDeviceId] = useStore<any>('selectedDeviceId')
  const [, setSelectedPetId] = useStore<any>('selectedPetId')
  const [filterName, setFilterName] = useState('')
  const isCompact = useIsCompact()

  // Fetch devices for this specific pet
  const { data, isLoading, hasCurrentDevices } = usePetDevicesWithHistory(selectedPetId)
  const { devicesWithMetrics, isLoading: metricsLoading } = useDevicesWithMetrics()

  // Filter to devices that have metrics data. Newest sensor first: the hook
  // fetches id ASC, which put a multi-sensor pet's OLDEST (usually expired)
  // sensor at the top — vets who didn't click down the list read stale data
  // (clinic feedback 2026-07-10). Devices without a start_time sort last.
  const devicesData = useMemo(
    () =>
      (data ?? [])
        .filter((d) => deviceHasMetrics(Number(d.id), devicesWithMetrics))
        .sort((a, b) => (b.start_time ?? 0) - (a.start_time ?? 0) || Number(b.id) - Number(a.id)),
    [data, devicesWithMetrics],
  )

  // Add pet-level virtual device if this pet has multiple devices
  const dataWithVirtual =
    devicesData.length === 0
      ? []
      : (() => {
          const rows: any[] = []

          if (devicesData.length > 1) {
            rows.push({
              id: getVirtualDeviceId(selectedPetId!),
              name: translate('resources.devices.all_devices_pet'),
              pet: selectedPetId,
              isVirtual: true,
              isPetVirtual: true,
            })
          }

          rows.push(...devicesData)
          return rows
        })()

  const filteredData = dataWithVirtual.filter((record: any) =>
    record.name?.toLowerCase().includes(filterName.toLowerCase()),
  )

  // NOTE: This component intentionally does NOT auto-select a default device.
  // It is mounted only inside PetGlucoseDetailPanel, which owns default
  // selection (its useEffect converges the shared `selectedDeviceId` store on
  // the pet-level virtual "All Devices" or the first device). A second
  // auto-selector here used a DIFFERENT device list (usePetDevicesWithHistory
  // filtered by /devices-with-metrics) than the panel's (useGetList of all
  // devices). When those two lists disagreed on count — e.g. a pet that just
  // got a 2nd sensor while /devices-with-metrics was still catching up — the
  // two effects picked conflicting defaults (single device vs virtual) and
  // overwrote each other, so the trend chart flickered between one sensor and
  // "All Devices (2)" and intermittently leaked under-constrained data. Letting
  // the panel be the single writer makes selection stable (a virtual id and any
  // in-list device both permanently "belong", so the panel can never flip it).
  // This grid now only changes selection on an explicit user click below.

  const handleRowClick =
    typeof rowClick === 'function'
      ? rowClick
      : (id: any, resource: any, record: any) => {
          // Handle "select" mode: only update selection, no navigation
          if (rowClick === 'select' || rowClick === '') {
            setSelectedDeviceId(String(record.id))
            // Also set the pet that owns this device
            const petId = typeof record.pet === 'object' ? record.pet.id : record.pet
            if (petId) {
              setSelectedPetId(String(petId))
            }
            return false // prevent navigation
          }
          // For other string modes (e.g., 'edit'), navigate to that view
          if (typeof rowClick === 'string' && rowClick) {
            return rowClick
          }
          // Fallback: no action
          return false
        }

  // Show loading state. Wait for BOTH the pet device list AND the
  // metrics-membership set to settle: deviceHasMetrics() treats a still-loading
  // membership set as "show all" (null → true), so rendering before it settles
  // would list every device and then drop the metric-less ones once it arrives
  // — a row-count flicker (esp. multi-sensor pets). Holding the loading state
  // until metricsLoading clears makes the grid resolve to its final row set in
  // one paint. The settled-null error fallback (endpoint down → show all) is
  // unaffected, since metricsLoading still clears in that case.
  if (isLoading || metricsLoading) {
    return <div>{translate('ra.page.loading')}</div>
  }

  // Show message when no devices found
  if (!selectedPetId || devicesData.length === 0) {
    return <div>{translate('pos.dashboard.no_device_selected')}</div>
  }

  // Below the md breakpoint the Datagrid columns overflow the viewport.
  // Render a tap-to-select card list instead, reusing the same handleRowClick
  // selection logic. Desktop (md+) renders the Datagrid below.
  if (isCompact) {
    return (
      <Stack spacing={1}>
        {filteredData.map((record: any) => {
          const selected = String(record.id) === String(selectedDeviceId)
          const petName =
            typeof record.pet === 'object'
              ? record.pet?.name || translate('resources.devices.pet_id', { id: record.pet?.id })
              : record.pet
          return (
            <Card
              key={record.id}
              variant="outlined"
              sx={{
                borderColor: selected ? 'primary.main' : 'divider',
                backgroundColor: selected ? 'rgba(79, 60, 201, 0.08)' : 'background.paper',
              }}
            >
              <CardActionArea
                onClick={() => handleRowClick(record.id, 'devices', record)}
                sx={{ p: 1.5 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {record.isPetVirtual && <LinkIcon sx={{ color: 'primary.main' }} />}
                  <Typography variant="subtitle1" sx={{ fontWeight: record.isVirtual ? 600 : 500 }}>
                    {record.name}
                  </Typography>
                  {record.isPetVirtual && (
                    <Chip
                      label={translate('resources.devices.devices_count', {
                        smart_count: devicesData.length,
                      })}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
                {!record.isVirtual && (
                  <Box
                    sx={{
                      mt: 0.5,
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr',
                      columnGap: 1,
                      rowGap: 0.25,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {translate('resources.devices.fields.start_time')}
                    </Typography>
                    <Typography variant="caption" sx={{ textAlign: 'right' }}>
                      {record.start_time
                        ? new Date(record.start_time * 1000).toLocaleString()
                        : translate('pos.common.empty_data')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {translate('resources.devices.fields.timezone')}
                    </Typography>
                    <Typography variant="caption" sx={{ textAlign: 'right' }}>
                      {formatTimezoneForDisplay(record.timezone) ||
                        translate('pos.common.empty_data')}
                    </Typography>
                  </Box>
                )}
              </CardActionArea>
            </Card>
          )
        })}
      </Stack>
    )
  }

  return (
    <Box>
      <Datagrid
        data={filteredData}
        resource="devices"
        rowClick={handleRowClick}
        bulkActionButtons={false}
        rowStyle={(record) =>
          String(record.id) === String(selectedDeviceId)
            ? { backgroundColor: 'rgba(79, 60, 201, 0.08)' }
            : {}
        }
      >
        <TextField source="id" />

        <FunctionField
          source="name"
          label={translate('resources.devices.fields.name')}
          render={(record) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {record.isPetVirtual && <LinkIcon sx={{ color: 'primary.main' }} />}
              <span style={{ fontWeight: record.isVirtual ? 600 : 400 }}>{record.name}</span>
              {record.isPetVirtual && (
                <Chip
                  label={translate('resources.devices.devices_count', {
                    smart_count: devicesData.length,
                  })}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        />

        <FunctionField
          label={translate('resources.devices.fields.pet')}
          render={(record) => {
            if (typeof record.pet === 'object') {
              return record.pet.name || translate('resources.devices.pet_id', { id: record.pet.id })
            }
            return record.pet
          }}
        />

        <FunctionField
          source="start_time"
          label={translate('resources.devices.fields.start_time')}
          render={(record) => {
            if (record.isVirtual) return '—'
            return record.start_time
              ? new Date(record.start_time * 1000).toLocaleString()
              : translate('pos.common.empty_data')
          }}
        />

        <FunctionField
          source="timezone"
          label={translate('resources.devices.fields.timezone')}
          render={(record) => {
            if (record.isVirtual) return '—'
            if (!record.timezone && record.timezone !== 0) {
              return translate('pos.common.empty_data')
            }

            const formatted = formatTimezoneForDisplay(record.timezone)
            return formatted || translate('pos.common.empty_data')
          }}
        />
      </Datagrid>
    </Box>
  )
}

export default EnhancedDeviceDatagrid

// For TypeScript compatibility, also export the type
export type { EnhancedDeviceDatagridProps }
